// Asistente flotante (D40, D41): "la mano derecha". Un boton abajo a la derecha, en todas las pantallas, abre una
// ventana en esa esquina que sigue abierta mientras navegas. Si tienes un caso o un correo abierto, el asistente lo
// sabe y su borrador se puede pasar a la respuesta.
// D59: con el aspecto del asistente de Cuentas (Kirby). Tarjeta de 440 px que crece desde el boton y vuelve a el, se
// estira desde la esquina de arriba a la izquierda (o "Agrandar") y en el celular ocupa toda la pantalla.
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { aCaso, type Caso, type RawCaso } from '../api/casos';
import { mensajesDeHilo } from '../api/correo';
import { gqlTodos } from '../api/gql';
import { CAMPOS_LISTA } from '../api/vistasCaso';
import { useSession } from '../auth/session';
import { modoLanzador } from '../lib/personaje/cara';
import { ponerAmix, useEstadoAmix } from '../lib/personaje/estado';
import { reducedMotion, SpringValue } from '../lib/spring';
import { readJSON, writeJSON } from '../lib/storage';
import { type ContextoCaso, type ContextoCorreo, PanelAsistente } from '../screens/bandeja/Asistente';
import { useCasosSinCerrar } from '../screens/bandeja/useVistas';
import { Amix } from './Amix';

/* ---------- estado compartido: abrir el asistente desde cualquier pantalla (con o sin un pedido) ---------- */
type Estado = { abierto: boolean; pedido: string | null };
let estado: Estado = { abierto: false, pedido: null };
const oyentes = new Set<() => void>();
const poner = (e: Partial<Estado>) => {
  estado = { ...estado, ...e };
  oyentes.forEach((f) => f());
};
export const abrirAsistente = (pedido?: string) => poner({ abierto: true, ...(pedido ? { pedido } : {}) });
const useEstado = () =>
  useSyncExternalStore(
    (f) => (oyentes.add(f), () => oyentes.delete(f)),
    () => estado,
  );

/* ---------- tamano de la ventana (escritorio) ---------- */
type Tamano = { ancho: number; alto: number } | null;
const CLAVE = 'consola.asistente.ventana';
const MIN = { ancho: 360, alto: 420 };
const NORMAL = () => ({ ancho: 440, alto: Math.min(700, window.innerHeight - 32) });
const GRANDE = () => ({ ancho: Math.min(860, Math.round(window.innerWidth * 0.6)), alto: window.innerHeight - 32 });
const ajustar = (t: { ancho: number; alto: number }) => ({
  ancho: Math.round(Math.min(Math.max(t.ancho, MIN.ancho), window.innerWidth - 32)),
  alto: Math.round(Math.min(Math.max(t.alto, MIN.alto), window.innerHeight - 32)),
});
const leerTamano = (): Tamano => {
  const t = readJSON<{ ancho?: number; alto?: number } | null>(CLAVE, null);
  return t && Number.isFinite(t.ancho) && Number.isFinite(t.alto) ? { ancho: t.ancho!, alto: t.alto! } : null;
};
const useEscritorio = () => {
  const [si, setSi] = useState(() => window.matchMedia('(min-width: 760px)').matches);
  useEffect(() => {
    const m = window.matchMedia('(min-width: 760px)');
    const f = () => setSi(m.matches);
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return si;
};

export function AsistenteFlotante() {
  const { yo } = useSession();
  const e = useEstado();
  const loc = useLocation();
  const ir = useNavigate();
  const amix = useEstadoAmix();
  const escritorio = useEscritorio();
  const [tamano, setTamano] = useState<Tamano>(leerTamano);
  // La ventana sigue montada mientras se cierra (vuelve al boton) y luego se quita.
  const [visible, setVisible] = useState(e.abierto);
  const caja = useRef<HTMLDivElement | null>(null);
  const resorte = useRef<SpringValue | null>(null);
  const boton = useRef<HTMLButtonElement | null>(null);

  // Enlaces viejos (?vista=asistente&pedir=...) abren la ventana con ese pedido.
  useEffect(() => {
    const sp = new URLSearchParams(loc.search);
    if (sp.get('vista') !== 'asistente') return;
    const pedido = sp.get('pedir');
    sp.delete('vista');
    sp.delete('pedir');
    ir(`${loc.pathname}${sp.toString() ? `?${sp}` : ''}`, { replace: true });
    abrirAsistente(pedido ?? undefined);
  }, [loc.search, loc.pathname, ir]);

  // El caso que tienes abierto (Bandeja -> Casos -> un caso).
  const casoId = loc.pathname.startsWith('/bandeja/casos') ? new URLSearchParams(loc.search).get('caso') : null;
  const sinCerrar = useCasosSinCerrar();
  const enLista = casoId ? (sinCerrar.data ?? []).find((c) => c.id === casoId) : undefined;
  const suelto = useQuery({
    queryKey: ['caso', 'contexto', casoId],
    queryFn: async () => (await gqlTodos<RawCaso>('casos', CAMPOS_LISTA, { filtro: { id: { eq: casoId } }, max: 1 })).map(aCaso)[0] ?? null,
    enabled: !!casoId && !enLista && !sinCerrar.isLoading,
    staleTime: 60_000,
  });
  const caso: Caso | null | undefined = enLista ?? suelto.data;
  const contexto: ContextoCaso = caso ? { casoId: caso.id, numero: caso.numero, asunto: caso.asunto, cliente: caso.empresa?.nombre || caso.persona?.nombre || caso.correoCliente } : null;

  // El correo que tienes abierto (Bandeja -> Correos -> un correo). Usa la misma consulta que la pantalla del correo.
  const hiloId = loc.pathname.startsWith('/bandeja/correos') ? new URLSearchParams(loc.search).get('hilo') : null;
  const hilo = useQuery({ queryKey: ['correos', 'hilo', hiloId], queryFn: () => mensajesDeHilo(hiloId!), enabled: !!hiloId, staleTime: 60_000 });
  const remitente = hilo.data?.[0]?.participantes.find((p) => String(p.role).toLowerCase() === 'from');
  const correo: ContextoCorreo = hiloId && hilo.data?.length ? { hiloId, asunto: hilo.data[hilo.data.length - 1].asunto, de: remitente?.displayName || remitente?.handle || '' } : null;

  // Abrir: crece desde el boton (abajo a la derecha) con un resorte sin rebote; el contenido aparece al final.
  useEffect(() => {
    if (e.abierto) {
      setVisible(true);
      ponerAmix('saludando');
    }
  }, [e.abierto]);
  useLayoutEffect(() => {
    const el = caja.current;
    if (!visible || !el || !e.abierto) return;
    if (reducedMotion()) return;
    resorte.current?.stop();
    resorte.current = new SpringValue(0, (p) => {
      el.style.opacity = String(Math.min(1, p * 1.6));
      el.style.transform = `scale(${0.6 + 0.4 * p})`;
    });
    resorte.current.go(1, { response: 0.35 });
  }, [visible, e.abierto]);
  const cerrar = useCallback(() => {
    const el = caja.current;
    poner({ abierto: false });
    let hecho = false;
    const fin = () => {
      if (hecho) return;
      hecho = true;
      setVisible(false);
      setTimeout(() => boton.current?.focus(), 0);
    };
    if (!el || reducedMotion()) return fin();
    resorte.current?.stop();
    resorte.current = new SpringValue(1, (p) => {
      el.style.opacity = String(Math.min(1, p * 1.6));
      el.style.transform = `scale(${0.6 + 0.4 * p})`;
    });
    resorte.current.go(0, { response: 0.28, done: fin });
    // Si la pestana no esta al frente el navegador pausa los cuadros de animacion: se cierra igual.
    setTimeout(fin, 450);
  }, []);
  useEffect(() => {
    if (!visible) return;
    const k = (ev: KeyboardEvent) => ev.key === 'Escape' && !document.querySelector('.dialog') && cerrar();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [visible, cerrar]);

  const guardar = (t: Tamano) => {
    const v = t ? ajustar(t) : null;
    setTamano(v);
    writeJSON(CLAVE, v);
  };
  const actual = tamano ?? NORMAL();
  const grande = actual.ancho >= 600;
  // Esquina de arriba a la izquierda: arrastrar cambia ancho y alto (la ventana esta anclada abajo a la derecha).
  const estirar = (ev: React.PointerEvent) => {
    const el = caja.current;
    if (!el) return;
    ev.preventDefault();
    const x0 = ev.clientX;
    const y0 = ev.clientY;
    const a0 = el.offsetWidth;
    const h0 = el.offsetHeight;
    let ultimo = { ancho: a0, alto: h0 };
    const mover = (m: PointerEvent) => {
      ultimo = ajustar({ ancho: a0 + (x0 - m.clientX), alto: h0 + (y0 - m.clientY) });
      el.style.width = `${ultimo.ancho}px`;
      el.style.height = `${ultimo.alto}px`;
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      guardar(ultimo);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };

  if (!yo.miembroId) return null;
  if (!visible)
    return (
      <button ref={boton} type="button" className="asis-boton" data-modo={modoLanzador(amix)} onClick={() => abrirAsistente()} title="Amix, tu asistente" aria-label="Abrir a Amix, el asistente de IA">
        <Amix estado={amix} size={54} mira />
        <span className="asis-marca" aria-hidden />
      </button>
    );

  return (
    <div
      ref={caja}
      className="amix-ventana"
      role="dialog"
      aria-label="Amix, asistente de IA"
      style={escritorio ? { width: actual.ancho, height: actual.alto } : undefined}
    >
      {escritorio ? (
        <div
          className="amix-esquina"
          role="separator"
          tabIndex={0}
          aria-label="Cambiar el tamaño de la ventana (doble clic: tamaño normal)"
          title="Arrastra para cambiar el tamaño · doble clic: tamaño normal"
          onPointerDown={estirar}
          onDoubleClick={() => guardar(null)}
          onKeyDown={(ev) => {
            const paso = ev.shiftKey ? 80 : 40;
            const d = { ArrowLeft: [paso, 0], ArrowRight: [-paso, 0], ArrowUp: [0, paso], ArrowDown: [0, -paso] }[ev.key];
            if (d) (ev.preventDefault(), guardar({ ancho: actual.ancho + d[0], alto: actual.alto + d[1] }));
          }}
        />
      ) : null}
      <div className="amix-dentro">
        <PanelAsistente
          contexto={contexto}
          correo={contexto ? null : correo}
          pedido={e.pedido}
          onPedidoHecho={() => poner({ pedido: null })}
          ventana={{ cerrar, grande, agrandar: escritorio ? () => guardar(grande ? null : GRANDE()) : undefined }}
        />
      </div>
    </div>
  );
}
